/**
 * Number formatting utilities — truncation, locale formatting, large numbers.
 *
 * Canonical implementations consolidated from:
 * - packages/ssm-web5/src/utils/numbers.js
 * - apps/ssm-web4/src/app/utils/numbers/index.js
 * - apps/ssm-web4/src/app/utils/amount/index.js
 */

import { isEnglishStyleLocale, normalizeLocaleTag } from '../dates';

/**
 * Truncate a number to N decimal places (floor toward zero, no rounding).
 *
 * @param {number} number
 * @param {number} decimals
 * @returns {number}
 */
export const truncate = (number, decimals) => {
    if (isNaN(number)) return number;
    if (!number) return number;
    if (!decimals) return number;

    const calcDec = Math.pow(10, decimals);
    return Math.trunc(number * calcDec) / calcDec;
};

/**
 * Format a number with locale grouping and fraction digits.
 *
 * @param {number} number
 * @param {string} locale — BCP 47 locale tag (e.g. "en-US", "es-ES")
 * @param {Intl.NumberFormatOptions} [options]
 * @returns {string}
 */
export const getLocaleFormat = (number, locale, options) => {
    const defaultOptions = { useGrouping: true, minimumFractionDigits: 2, ...(options || {}) };
    return number.toLocaleString(normalizeLocaleTag(locale) || locale, defaultOptions);
};

/**
 * Format a number with locale-aware group separators.
 * Returns "0" for falsy input; returns the number unmodified if no locale given.
 *
 * @param {number} number
 * @param {string} [locale]
 * @returns {string}
 */
export const addNumberSeparators = (number, locale) => {
    if (!number) return '0';
    if (!locale) return number;
    return number.toLocaleString(normalizeLocaleTag(locale) || locale);
};

/**
 * Convert a value to a locale-formatted number string.
 *
 * API decision: includes the optional `useAllDecimals` flag (web4 superset).
 * When `true`, the formatted string preserves all significant decimal digits.
 *
 * @param {number|string} value
 * @param {Object} [options] — passed to `Number.toLocaleString`
 * @param {boolean} [useAllDecimals=false]
 * @param {string} [locale='en-US'] — BCP 47 locale tag
 * @returns {string|*}
 */
export const getNumberAsLocaleNumber = (
    value,
    options,
    useAllDecimals = false,
    locale = 'en-US',
) => {
    try {
        if (!value) return value;
        if (isNaN(value)) {
            value = value.replace(',', '.');
            if (isNaN(value)) return value;
        }

        options = { useGrouping: true, ...options };

        if (useAllDecimals) {
            const numStr = value.toString();
            const trimmedStr = numStr.replace(/(\.\d*?[1-9])0+$/, '$1');
            const decimalCount = trimmedStr.includes('.') ? trimmedStr.split('.')[1].length : 0;

            if (decimalCount > 0) {
                options.minimumFractionDigits = decimalCount;
            }
        }

        return Number(value).toLocaleString(normalizeLocaleTag(locale) || locale, options);
    } catch (ex) {
        console.warn('something wrong happens when formatting value ', value);
        return value;
    }
};

/**
 * Returns `true` for locales that use a decimal point (.) and place the
 * currency symbol at the left (English-style formatting).
 * @param {string} locale
 * @returns {boolean}
 */
const isLocaleSpecialFormat = (locale) => isEnglishStyleLocale(locale);

/**
 * Format a number with thousands separators appropriate for the given locale.
 *
 * API decision: takes an explicit `locale` parameter (pure function) instead
 * of reading global state.
 *
 * @param {number|string} number
 * @param {string} locale — e.g. "en-US", "es-ES"
 * @returns {string}
 */
export const formatNumberWithSeparator = (number, locale) => {
    let parts;
    let formatedNumber;
    if (isLocaleSpecialFormat(locale)) {
        parts = number.toString().split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        formatedNumber = parts.join('.');
    } else {
        parts = number.toString().split(',');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        formatedNumber = parts.join(',');
    }
    return formatedNumber;
};

/**
 * Convert a number string to the locale-appropriate decimal format.
 * @param {string|number} number
 * @param {string} locale
 * @returns {string}
 */
const getLocaleDecimalNumberFormat = (number, locale) => {
    if (number && !isNaN(number)) {
        number = number.toString();
        if (isLocaleSpecialFormat(locale)) {
            number = number.replace(',', '.');
        } else {
            number = number.replace('.', ',');
        }
        return number;
    }
    return number;
};

/**
 * Format a large number with unit suffixes (K, M, B) and locale-aware
 * separators.
 *
 * API decision: uses object-parameter signature from web5 for clarity.
 *
 * @param {Object} params
 * @param {number|string} params.number
 * @param {number} [params.decimals=2]
 * @param {string} params.locale — BCP 47 locale tag
 * @returns {string}
 */
export const formatLargeNumber = ({ number, decimals = 2, locale }) => {
    if (isNaN(number)) return number;
    number = parseFloat(number);
    const isNegative = number < 0;
    number = Math.abs(number);

    const finalDecimals = decimals * 10;

    let digits = 0;
    if (number >= 1000 && number < 100000) {
        digits = 1;
    } else if (number >= 100000 && number < 1000000) {
        digits = 0;
    } else if (number >= 1000000 && number < 100000000) {
        digits = 1;
    }

    const units = ['', 'K', 'M', 'B'];
    let decimal;
    let unitsIndex = 0;
    let finalValue = '';

    for (let i = units.length - 1; i >= 0; i--) {
        decimal = Math.pow(1000, i);

        if (number <= -decimal || number >= decimal) {
            unitsIndex = i;
            finalValue = truncate(number / decimal, digits);
            break;
        }
    }

    if (finalValue > 10e21 || finalValue < 10e-20) {
        finalValue = Number(finalValue).toPrecision(2);
    }

    if (isNegative) finalValue = parseFloat(finalValue) * -1;
    finalValue = Math.round(finalValue * finalDecimals) / finalDecimals;
    finalValue = formatNumberWithSeparator(
        getLocaleDecimalNumberFormat(finalValue, locale),
        locale,
    );
    return finalValue + units[unitsIndex];
};

/**
 * Format a number into a short, human-readable form with unit suffixes
 * (K, M, B). Supports locale-aware decimal formatting.
 *
 * @param {number|string} number
 * @param {string} [locale]
 * @param {number} [forceDigits] — force a specific number of decimal digits
 * @returns {string}
 */
export const getShortFormat = (number, locale, forceDigits) => {
    if (isNaN(parseInt(number, 10))) return number;
    if (typeof number === 'string') number = number.replace(',', '.');
    number = parseFloat(number);
    const isNegative = number < 0;
    number = Math.abs(number);

    let digits = 0;
    if (forceDigits) digits = forceDigits;
    else {
        if (number >= 1000 && number < 100000) {
            digits = 1;
        } else if (number >= 100000 && number < 1000000) {
            digits = 0;
        } else if (number >= 1000000 && number < 100000000) {
            digits = 1;
        } else if (number > 0 && number < 1) {
            digits = 2;
        }
    }

    const units = ['', 'K', 'M', 'B'];
    let decimal;
    let unitsIndex = 0;
    let finalValue = '';

    for (let i = units.length - 1; i >= 0; i--) {
        decimal = Math.pow(1000, i);

        if (number <= -decimal || number >= decimal) {
            unitsIndex = i;
            finalValue = truncate(number / decimal, digits);
            break;
        }
    }

    // 0 with decimals
    if ((number || number === 0) && !finalValue) finalValue = number;
    if (number !== 0 && (finalValue > 10e21 || finalValue < 10e-20) && !forceDigits) {
        finalValue = Number(finalValue).toPrecision(2);
    }

    if (isNegative) finalValue = parseFloat(finalValue) * -1;

    if (locale) {
        finalValue = getLocaleFormat(
            finalValue,
            locale,
            forceDigits ? { minimumFractionDigits: 2, maximumFractionDigits: digits } : undefined,
        );
    } else {
        finalValue = finalValue.toLocaleString(
            undefined,
            forceDigits ? { minimumFractionDigits: 2, maximumFractionDigits: digits } : undefined,
        );
    }

    return `${finalValue}${units[unitsIndex]}`;
};

export {
    getDecimalFormat,
    getNumberFormat,
    getPercentageFormat,
    parseLocalizedDecimalInput,
} from './locale-decimal-format';
